import type { ReactNode } from 'react';
import {
    EQUIPMENT_SUBSECTION_LABEL,
    PRACTICE_DETAIL_GROUPS,
    type PracticeFieldGroup
} from './fieldDefinitions';
import { DetailGroupCard } from './DetailGroupCard';
import type { DetailFieldGridProps } from './DetailFieldGrid';

interface FieldAccessoryEntry {
    key: string;
    value: string;
    /** Shown while the field is empty (e.g. "Request Spark account"). */
    emptyAction?: ReactNode;
    /** Shown once the field holds a value (e.g. SparkCredsAction). */
    filledAction?: ReactNode;
}

// Builds the Account card's fieldAccessories map, keyed by field so an entry
// keeps matching if a field ever moves cards; re-derived from the live form
// value every render, so an accessory swaps the instant its field changes.
function fieldAccessories(
    group: PracticeFieldGroup,
    entries: FieldAccessoryEntry[]
): Partial<Record<string, ReactNode>> | undefined {
    const accessories: Partial<Record<string, ReactNode>> = {};
    for (const entry of entries) {
        const hasField = group.fields.some((field) => field.key === entry.key);
        if (!hasField) continue;
        const action = entry.value.trim().length === 0 ? entry.emptyAction : entry.filledAction;
        if (action) accessories[entry.key] = action;
    }
    return Object.keys(accessories).length > 0 ? accessories : undefined;
}

interface AccountSectionProps extends Omit<
    DetailFieldGridProps,
    'fields' | 'fieldAccessories' | 'showRequiredMarkers' | 'extraCells'
> {
    billComAction: ReactNode;
    dmLinkAction: ReactNode;
    dmChangeAction: ReactNode;
    sparkAccountAction: ReactNode;
    sparkCredsAction: ReactNode;
    iteroConnectionField: ReactNode;
}

// The Details tab's "Account" pane: billId/sparkAcct accessories, and the iTero box as an Equipment cell.
// The surrounding page scrolls now, so this owns none of its own.
export function AccountSection({
    billComAction,
    dmLinkAction,
    dmChangeAction,
    sparkAccountAction,
    sparkCredsAction,
    iteroConnectionField,
    ...gridProps
}: AccountSectionProps) {
    const group = PRACTICE_DETAIL_GROUPS.find((g) => g.id === 'account');
    if (!group) return null;

    return (
        <DetailGroupCard
            group={group}
            {...gridProps}
            fieldAccessories={fieldAccessories(group, [
                { key: 'billId', value: gridProps.values.billId ?? '', emptyAction: billComAction },
                {
                    key: 'dmDoctorId',
                    value: gridProps.values.dmDoctorId ?? '',
                    emptyAction: dmLinkAction,
                    filledAction: dmChangeAction
                },
                {
                    key: 'sparkAcct',
                    value: gridProps.values.sparkAcct ?? '',
                    emptyAction: sparkAccountAction,
                    filledAction: sparkCredsAction
                }
            ])}
            subsectionCells={{ [EQUIPMENT_SUBSECTION_LABEL]: iteroConnectionField }}
        />
    );
}
